import { useState, useContext } from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTopControls,
    SttTable,
    SttButtonFab,
    StatusDot,
    SttPage,
} from '../../../sporttia/all';
import { useCrud, useToggle, useTuples } from '../../../lib/hooks';
import FacilitySet from './FacilitySet';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

export default function FacilitySets(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [facilitySets, setFacilitySets, , Get] = useCrud<any>();
    const [facilitySetIdAndName, setFacilitySetIdAndName] = useState<{
        id: string | number;
        name: string;
    } | null>(null);

    const [filters, setFilter] = useTuples<{ status: string; name: string }>({
        status: 'ALL',
        name: '',
    });

    // Trash hook
    const [trash, toggleTrash] = useToggle(() => {
        loadFacilitySets();
    });

    /**
     * Get facility sets
     */
    function loadFacilitySets(params = { page: 1, rows: 20 }) {
        Get<any>(`/scs/${cxt?.sc?.id}/facilitysets`, {
            name: filters.name,
            status: filters.status === 'ALL' ? '' : filters.status,
            ...params,
            trash,
        }).then((response: any) => {
            setFacilitySets(response);
        });
    }

    function goCreateNew() {
        setFacilitySetIdAndName({ id: 'create', name: '' });
    }

    return (
        <>
            <SttPage title={t('FacilityGroups')}>
                <SttTopControls
                    p={2}
                    fields={
                        [
                            {
                                name: 'name',
                                type: 'text',
                                value: filters.name,
                                caption: t('Name'),
                                onEnterKey: loadFacilitySets,
                                xs: 12,
                                sm: 12,
                                md: 12,
                            },
                        ] as any
                    }
                    extraFields={
                        [
                            {
                                xs: 12,
                                sm: 6,
                                md: 3,
                                name: 'status',
                                type: 'select',
                                value: filters.status,
                                options: [
                                    {
                                        caption: t('All'),
                                        value: 'ALL',
                                    },
                                    {
                                        caption: t('Actives'),
                                        value: 'ACTIVE',
                                    },
                                ],
                            },
                        ] as any
                    }
                    onChange={setFilter}
                    onFilter={() => loadFacilitySets()}
                    onToggleTrash={toggleTrash}
                />

                <SttTable
                    data={facilitySets}
                    loading={false}
                    columns={[
                        {
                            title: '',
                            width: 40,
                            value: (row: any) => (
                                <StatusDot active={row.status === 'ACTIVE'} />
                            ),
                        },
                        {
                            field: 'created',
                            title: t('Created'),
                            value: (r: any) =>
                                new DateBuilder(
                                    r?.created,
                                    cxt?.sc?.timezone?.name,
                                    cxt?.lang?.key,
                                ).dmy(),
                        },
                        {
                            field: 'name',
                            title: t('Name'),
                            align: 'left',
                            type: 'link',
                            onClick: (row: any) => {
                                setFacilitySetIdAndName({
                                    id: row.id,
                                    name: row.name,
                                });
                            },
                        },
                        {
                            title: t('facility.facilities'),
                            value: (row: any) => (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    flexWrap="wrap"
                                    gap={0.5}
                                >
                                    {row.facilities?.rows?.map(
                                        (facility: any) => (
                                            <Tooltip
                                                key={facility.id}
                                                title={facility.name}
                                            >
                                                <Chip
                                                    label={facility.name
                                                        .charAt(0)
                                                        .toUpperCase()}
                                                    size="small"
                                                    sx={{
                                                        minWidth: 28,
                                                        height: 24,
                                                        '& .MuiChip-label': {
                                                            px: 0.5,
                                                        },
                                                    }}
                                                />
                                            </Tooltip>
                                        ),
                                    )}
                                </Stack>
                            ),
                        },
                        {
                            field: 'position',
                            type: 'position',
                            title: t('Position'),
                        },
                    ]}
                    onFetch={loadFacilitySets}
                />
                <SttButtonFab
                    onClick={goCreateNew}
                    permission={PERMISSIONS.FACILITY.MODIFY}
                />
            </SttPage>

            {facilitySetIdAndName && (
                <FacilitySet
                    facilitySetId={facilitySetIdAndName.id}
                    onClose={() => {
                        setFacilitySetIdAndName(null);
                    }}
                    title={facilitySetIdAndName.name}
                />
            )}
        </>
    );
}
